/* eslint-disable react/no-multi-comp */
import Image from 'next/image';

import clsx from 'clsx';

//hooks
import useTranslation from '@/utils/hooks/translation/useTranslation';

//utils
import useDarkMode from '@/utils/hooks/utils/useDarkMode';

//types
import type { SubscriptionType } from '@/types/pricingTypes/subscriptionType';

//apiEndpoints
import { FaqCTA } from '@/components/pages/settings/subscription-invoices/PageContent/Components/FaqCTA';

import Button from '@/components/base/Button';
import H2 from '@/components/base/Headings/H2';
//components
import { RightSection } from '@/components/pages/settings/subscription-invoices/PageContent/Components/RightSection';
import { Trans } from '@/components/tools/translation';
import SettingsWrapper from '@/components/layouts/LayoutPlatform/components/SettingsWrapper';

import JellycoGlassesDark from './../../../../../../public/images/pricing/jellyco-glasses-dark.webp';
//images
import JellycoGlassesLight from './../../../../../../public/images/pricing/jellyco-glasses-light.webp';
import JellycoSweatDark from './../../../../../../public/images/pricing/jellyco-sweat-dark.webp';
import JellycoSweatLight from './../../../../../../public/images/pricing/jellyco-sweat-light.webp';

type NoSubscriptionProps = { subscription: SubscriptionType; togglePlanModal: () => void };

type ChoosePlanActionType = {
    onClick: NoSubscriptionProps['togglePlanModal'];
};
function ChoosePlanAction({ onClick }: ChoosePlanActionType) {
    const { t } = useTranslation('settings');

    return (
        <div className="mt-10 flex flex-col items-center space-y-3">
            <Button variant="primary" size="xl" onClick={onClick} data-testid="button-choose-plan">
                {t('subscription_invoices_page.choose_your_plan')}
            </Button>
            <FaqCTA data-testid="choose-plan-faq-link" />
        </div>
    );
}

function NoTrialNeverHadPlan({
    togglePlanModal,
    className
}: Pick<NoSubscriptionProps, 'togglePlanModal'> & { className?: string }) {
    const { t } = useTranslation('settings');
    const { isDarkMode } = useDarkMode();
    const ImageSrc = isDarkMode() ? JellycoGlassesDark : JellycoGlassesLight;
    return (
        <div
            className={clsx('flex flex-1 flex-col justify-center', className)}
            data-testid="no-subscription-no-plan-history">
            <div className="flex flex-1 flex-col items-center justify-center">
                <Image src={ImageSrc} width={168} height={175} alt="no active plan illustration" />
                <div className="mt-8 flex flex-col items-center space-y-2.5">
                    <H2>
                        {t('subscription_invoices_page.new_pricing.no_subscription.trial_ended')}
                    </H2>
                    <p className="text-center font-GMedium text-icoGray-500 dark:text-icoDarkMode-wolf">
                        <Trans
                            i18nKey="settings:subscription_invoices_page.new_pricing.no_subscription.trial_ended_notice"
                            components={{ br: <br /> }}
                        />
                    </p>
                </div>
                <ChoosePlanAction onClick={togglePlanModal} />
            </div>
        </div>
    );
}

function NoSubscriptionContent({ togglePlanModal }: Pick<NoSubscriptionProps, 'togglePlanModal'>) {
    const { t } = useTranslation('settings');
    const { isDarkMode } = useDarkMode();
    const ImageSrc = isDarkMode() ? JellycoSweatDark : JellycoSweatLight;
    return (
        <div className="flex flex-col items-center">
            <Image src={ImageSrc} width={168} height={175} alt="no active plan illustration" />
            <div className="mt-8 flex flex-col items-center">
                <H2>
                    {t('subscription_invoices_page.new_pricing.no_subscription.previous_plan_end')}
                </H2>
                <p className="text-center font-GMedium text-icoGray-500 dark:text-icoDarkMode-wolf">
                    <Trans
                        i18nKey="settings:subscription_invoices_page.new_pricing.no_subscription.previous_plan_end_notice"
                        components={{ br: <br /> }}
                    />
                </p>
            </div>
            <ChoosePlanAction onClick={togglePlanModal} />
        </div>
    );
}

export function NoSubscription({ subscription, togglePlanModal }: NoSubscriptionProps) {
    if (subscription?.no_trial_never_had_plan) {
        return (
            <SettingsWrapper.MainContent>
                <NoTrialNeverHadPlan togglePlanModal={togglePlanModal} />
            </SettingsWrapper.MainContent>
        );
    }

    return (
        <>
            <SettingsWrapper.MainContent>
                <NoSubscriptionContent togglePlanModal={togglePlanModal} />
            </SettingsWrapper.MainContent>
            <RightSection togglePlanModal={togglePlanModal} />
        </>
    );
}
